import { type Eip1193Provider, BrowserProvider, getAddress } from 'ethers'
import {
  CHAIN_ID,
  MAIN_SAFE,
  MODULE_SAFE,
  MULTI_SEND_CALL_ONLY,
} from './constants.js'
import {
  computeSafeTxHash,
  emptyGasParams,
  safeTxTypedData,
  wrapAsModuleCall,
  wrapAsMultiSendModuleCall,
  type BatchCall,
  type SafeTx,
} from './safe.js'
import {
  buildInnerSignature,
  computeInnerPersonalSignHash,
  computeInnerTypedDataHash,
  encodeNestedContractSignature,
  signSafeMessageAsOwner,
  type EthSignTypedDataPayload,
} from './signMessage.js'
import { getSafeInfo, getMultisigTransaction, proposeTx } from './safeTxService.js'
import { SafewardClient } from '../safeward-client.js'
import type { Hex } from '../types.js'

/**
 * Handle a dApp `eth_sendTransaction` or `wallet_sendCalls` request.
 *
 * The dApp thinks it's talking to the Main Safe (`MAIN_SAFE`). What we
 * actually do:
 *
 *   1. Wrap the call(s) as `execTransactionFromModule(target, value, data, op)`
 *      targeting the Main Safe. Single call → CALL. Batch → DELEGATECALL into
 *      MultiSendCallOnly.
 *   2. Build a Safe transaction for the *Module Safe* (its `to` is the Main
 *      Safe), fetching the next nonce from Safe Tx Service.
 *   3. Ask Rabby (the connected AppKit signer = Ainura's EOA) to sign the
 *      EIP-712 SafeTx hash via `eth_signTypedData_v4`.
 *   4. POST the proposed tx to Safe Tx Service with Ainura's sig.
 *   5. Trigger the Guardian co-sign via the existing `/v1/sign-pending`.
 *   6. Return `safeTxHash` to the dApp as the tx hash / sendCalls id.
 */

export interface DAppTxRequest {
  to: string
  value?: string
  data?: Hex
}

export interface HandlerDeps {
  /** EIP-1193 provider returned from useAppKitProvider('eip155'). */
  provider: Eip1193Provider
  /** The address connected via AppKit — must be a Module Safe owner. */
  signerAddress: string
  /** Optional safeward client for triggering Guardian co-sign. */
  safeward?: SafewardClient
}

export interface HandlerResult {
  /** safeTxHash returned to the dApp as the "tx hash". */
  safeTxHash: Hex
  /** Whether the Guardian co-sign succeeded (not the on-chain execution). */
  guardianSigned: boolean
  /** Reason the Guardian rejected, if any. */
  guardianReason?: string
}

async function signProposeAndTriggerGuardian(
  safeTx: SafeTx,
  deps: HandlerDeps,
): Promise<HandlerResult> {
  // Ask Rabby for the typed-data signature.
  const typed = safeTxTypedData(CHAIN_ID, MODULE_SAFE, safeTx)
  const browser = new BrowserProvider(deps.provider, CHAIN_ID)
  const signer = await browser.getSigner(getAddress(deps.signerAddress))
  const signature = (await signer.signTypedData(
    typed.domain,
    typed.types,
    typed.message,
  )) as Hex

  // Propose to Safe Tx Service.
  const safeTxHash = computeSafeTxHash(CHAIN_ID, MODULE_SAFE, safeTx)
  await proposeTx(MODULE_SAFE, {
    ...safeTx,
    contractTransactionHash: safeTxHash,
    sender: getAddress(deps.signerAddress),
    signature,
    origin: 'safeward-ui',
  })

  // Best-effort Guardian co-sign — failure here doesn't block return.
  let guardianSigned = false
  let guardianReason: string | undefined
  if (deps.safeward) {
    try {
      const r = await deps.safeward.signPending(safeTxHash, MODULE_SAFE)
      guardianSigned = r.signed && r.posted
      if (!guardianSigned) guardianReason = r.decision.reason
    } catch (err) {
      guardianReason = (err as Error).message
    }
  }

  return { safeTxHash, guardianSigned, guardianReason }
}

/** Single-call path (`eth_sendTransaction`). */
export async function handleDAppTransaction(
  req: DAppTxRequest,
  deps: HandlerDeps,
): Promise<HandlerResult> {
  const wrapped = wrapAsModuleCall(MAIN_SAFE, {
    to: req.to,
    value: BigInt(req.value ?? '0'),
    data: (req.data ?? '0x') as Hex,
  })
  const moduleInfo = await getSafeInfo(MODULE_SAFE)
  const safeTx: SafeTx = {
    to: wrapped.to,
    value: wrapped.value,
    data: wrapped.data,
    operation: 0,
    ...emptyGasParams(),
    nonce: moduleInfo.nonce,
  }
  return signProposeAndTriggerGuardian(safeTx, deps)
}

/**
 * Off-chain EIP-1271 sign-message pipeline (`personal_sign`,
 * `eth_signTypedData_v4`).
 *
 * The Main Safe is a contract — it can't ECDSA-sign. Its sole owner is the
 * Module Safe, which is itself a 2/2 (Ainura + Guardian). We satisfy
 * Main.isValidSignature by:
 *   1. Computing innerHash for the dApp's request.
 *   2. Asking Ainura's wallet to sign SafeMessage(innerHash) over the
 *      *Module Safe's* EIP-712 domain.
 *   3. Asking the worker to co-sign with Guardian's key (after running
 *      the smart-permit policy server-side).
 *   4. Sorting + concatenating the two ECDSA sigs → innerSig.
 *   5. Wrapping innerSig as a Safe contract-signature pointing at the
 *      Module Safe → outerSig (227 bytes).
 *
 * Pure off-chain. No gas. No Module Safe tx queued. The dApp gets back
 * outerSig directly and any EIP-1271 verifier can validate it via
 * Main.isValidSignature(innerHash, outerSig).
 */
export interface SignMessageHandlerResult {
  /** 227-byte nested contract signature ready to return to the dApp. */
  outerSig: Hex
  /** Worker's policy decision text (used for logging / UI hints). */
  policyDetail: string
}

export class SignMessagePolicyRefused extends Error {
  constructor(public readonly detail: string) {
    super(`Guardian refused to sign: ${detail}`)
    this.name = 'SignMessagePolicyRefused'
  }
}

async function runSignMessagePipeline(
  innerHash: Hex,
  workerRequest:
    | { method: 'personal_sign'; message: string }
    | { method: 'eth_signTypedData_v4'; payload: EthSignTypedDataPayload },
  deps: HandlerDeps,
): Promise<SignMessageHandlerResult> {
  if (!deps.safeward) {
    throw new Error(
      'Sign-message requires the safeward worker (deps.safeward is missing)',
    )
  }
  const ownerSig = await signSafeMessageAsOwner(
    deps.provider,
    deps.signerAddress,
    CHAIN_ID,
    MODULE_SAFE,
    innerHash,
  )
  const guardianResp = await deps.safeward.signMessage({
    innerHash,
    request: workerRequest,
  })
  if (!guardianResp.signed) {
    throw new SignMessagePolicyRefused(guardianResp.decision.detail)
  }
  const innerSig = buildInnerSignature([
    { owner: deps.signerAddress, signature: ownerSig },
    {
      owner: guardianResp.signerAddress,
      signature: guardianResp.signature as Hex,
    },
  ])
  const outerSig = encodeNestedContractSignature(MODULE_SAFE, innerSig)
  return { outerSig, policyDetail: guardianResp.decision.detail }
}

export async function handleDAppPersonalSign(
  message: string,
  deps: HandlerDeps,
): Promise<SignMessageHandlerResult> {
  const innerHash = computeInnerPersonalSignHash(message)
  return runSignMessagePipeline(
    innerHash,
    { method: 'personal_sign', message },
    deps,
  )
}

export async function handleDAppSignTypedData(
  payload: EthSignTypedDataPayload,
  deps: HandlerDeps,
): Promise<SignMessageHandlerResult> {
  const innerHash = computeInnerTypedDataHash(payload)
  return runSignMessagePipeline(
    innerHash,
    { method: 'eth_signTypedData_v4', payload },
    deps,
  )
}

/** Batched path (`wallet_sendCalls` / EIP-5792). */
export async function handleSendCalls(
  calls: BatchCall[],
  deps: HandlerDeps,
): Promise<HandlerResult> {
  if (calls.length === 0) throw new Error('wallet_sendCalls: empty calls array')

  // Single call → simpler CALL path. Atomicity is trivially the same.
  if (calls.length === 1) {
    const c = calls[0]!
    return handleDAppTransaction(
      {
        to: c.to,
        value: c.value !== undefined ? c.value.toString() : '0',
        data: c.data,
      },
      deps,
    )
  }

  const wrapped = wrapAsMultiSendModuleCall(
    MAIN_SAFE,
    MULTI_SEND_CALL_ONLY,
    calls,
  )
  const moduleInfo = await getSafeInfo(MODULE_SAFE)
  const safeTx: SafeTx = {
    to: wrapped.to,
    value: wrapped.value,
    data: wrapped.data,
    operation: 0, // outer is a CALL into the Main Safe; the DELEGATECALL is inside
    ...emptyGasParams(),
    nonce: moduleInfo.nonce,
  }
  return signProposeAndTriggerGuardian(safeTx, deps)
}

/**
 * EIP-5792 `wallet_getCapabilities`. We advertise atomic batching for the
 * Main Safe on mainnet (everything routes through Module Safe → MultiSend).
 */
export function getCapabilitiesFor(
  address: string,
): Record<string, Record<string, { status: string }>> {
  if (getAddress(address) !== getAddress(MAIN_SAFE)) return {}
  return {
    [`0x${CHAIN_ID.toString(16)}`]: {
      atomic: { status: 'supported' },
    },
  }
}

/**
 * EIP-5792 `wallet_getCallsStatus`. Look up the safeTxHash (which we
 * returned as the call bundle id) in Safe Tx Service and translate to
 * the EIP-5792 status code:
 *   - 100 PENDING (not yet executed)
 *   - 200 CONFIRMED (executed, success)
 *   - 500 FAILED (executed, reverted)
 *
 * Throws "UnknownBundleId" if the id isn't a safeTxHash we'd have issued
 * (or if Safe Tx Service has no record of it). Per EIP-5792, returning an
 * error is the right way to make the dApp stop polling — returning 100
 * causes infinite re-poll loops we've seen in the wild from dApps that
 * cache stale call IDs across page reloads.
 */
export async function handleGetCallsStatus(callId: string): Promise<{
  version: string
  id: string
  chainId: string
  atomic: boolean
  status: number
  receipts?: Array<{
    logs: never[]
    status: string
    blockHash: string
    blockNumber: string
    gasUsed: string
    transactionHash: string
  }>
}> {
  // Cheap reject: if it's not a 0x-prefixed 32-byte hex string, it's not
  // something we ever issued. Don't even hit Safe Tx Service.
  if (!/^0x[0-9a-fA-F]{64}$/.test(callId)) {
    throw new Error(
      `Unknown bundle id (not a safeTxHash): ${callId.slice(0, 32)}…`,
    )
  }

  const tx = await getMultisigTransaction(callId)
  if (!tx) {
    // Valid hash format but Safe Tx Service has no record. Could be a
    // stale id from before a reset, or a bundle that never actually got
    // proposed. Tell the dApp it's unknown so it stops polling.
    throw new Error(`Unknown bundle id (not in Safe Tx Service): ${callId}`)
  }

  let status = 100
  const receipts: Array<{
    logs: never[]
    status: string
    blockHash: string
    blockNumber: string
    gasUsed: string
    transactionHash: string
  }> = []
  if (tx.isExecuted) {
    if (tx.isSuccessful === false) {
      status = 500
    } else {
      status = 200
      if (tx.transactionHash) {
        receipts.push({
          logs: [],
          status: '0x1',
          blockHash: '0x',
          blockNumber: '0x0',
          gasUsed: '0x0',
          transactionHash: tx.transactionHash,
        })
      }
    }
  }
  return {
    version: '2.0.0',
    id: callId,
    chainId: `0x${CHAIN_ID.toString(16)}`,
    atomic: true,
    status,
    receipts: receipts.length > 0 ? receipts : undefined,
  }
}
